import { MOBILE_EXAMPLES } from 'apps/promo/constants/libphonenumberPhoneExamples';

type LibPhoneNumber = {
    getExampleNumber: (region: string, examples: unknown) => { formatInternational: () => string; countryCallingCode: string | number } | null;
    parsePhoneNumber: (value: string, iso2: string) => { formatInternational: () => string } | null;
};
type WindowWithLibphonenumber = Window & { libphonenumber?: LibPhoneNumber };

export const DEFAULT_MASK = '... ... ... ... ..';

const maskCache: Record<string, string> = {};

const getLibphonenumber = (): LibPhoneNumber | null => {
    if (typeof window === 'undefined') return null;
    return (window as WindowWithLibphonenumber).libphonenumber ?? null;
};

// Example: "7440 000031" -> ".... ...... .." or "xxx xxx xxxx" -> "... ... ...."
export const toMask = (templateString?: string): string => {
    return (
        (templateString || '')
            .replace(/\u00A0/g, ' ')
            .replace(/\d/g, '.')
            .replace(/x/g, '.')
            .trim() || DEFAULT_MASK
    );
};

export const getMobileIntlMaskForCountry = (iso2?: string): Promise<string> => {
    const region = (iso2 || '').toUpperCase();
    if (!region) return Promise.resolve(DEFAULT_MASK);
    if (maskCache[region]) {
        return Promise.resolve(maskCache[region]);
    }
    try {
        const lib = getLibphonenumber();
        if (!lib) return Promise.resolve(DEFAULT_MASK);

        const exampleNumber = lib.getExampleNumber(region, MOBILE_EXAMPLES);
        if (exampleNumber) {
            const internationalFormat = exampleNumber.formatInternational();
            const countryCallingCode = exampleNumber.countryCallingCode;
            const nationalPart = internationalFormat.replace(new RegExp(`^\\+${countryCallingCode}\\s*`), '');
            const mask = toMask(nationalPart);
            if (mask && mask.includes('.')) {
                maskCache[region] = mask;
                return Promise.resolve(mask);
            }
        }
        return Promise.resolve(DEFAULT_MASK);
    } catch {
        return Promise.resolve(DEFAULT_MASK);
    }
};

export const buildMasksObject = (iso2OrList?: string | string[]): Record<string, Promise<string>> => {
    const list = Array.isArray(iso2OrList) ? iso2OrList : [iso2OrList];
    return list.reduce<Record<string, Promise<string>>>((acc, code) => {
        if (code) acc[code] = getMobileIntlMaskForCountry(code);
        return acc;
    }, {});
};

export const formatIntlPhoneNumber = (rawNumber: string, iso2: string): string => {
    if (!rawNumber || !iso2) return rawNumber;
    const lib = getLibphonenumber();
    if (!lib) {
        return rawNumber;
    }
    try {
        const phoneNumber = lib.parsePhoneNumber(`+${rawNumber}`, iso2.toUpperCase());
        return phoneNumber?.formatInternational() ?? rawNumber;
    } catch {
        return rawNumber;
    }
};
